/* ============================================================
   Wido Ops — backend API client (scaffold).

   The app currently renders from the in-memory mock in `src/data.js`.
   To move onto the live Flask backend, replace the mock reads with the
   async calls below and feed the results into the same shapes the UI
   already consumes (see `window.WidoData` in src/data.js for the target
   shapes). Endpoints here mirror WidoOpsBackend/SPEC.md.

   Base URL: in dev, Vite proxies `/api` → http://localhost:5000
   (see vite.config.js). In prod, set VITE_API_BASE_URL.
   ============================================================ */

const BASE = (import.meta.env.VITE_API_BASE_URL || "") + "/api";

async function http(method, path, body) {
  const res = await fetch(BASE + path, {
    method,
    headers: { "Content-Type": "application/json" },
    credentials: "include", // send the session cookie set by Google SSO
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) {
    // not signed in / session expired — let the app route to the login screen
    throw Object.assign(new Error("unauthorized"), { status: 401 });
  }
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw Object.assign(new Error(`${method} ${path} -> ${res.status}`), { status: res.status, detail });
  }
  return res.status === 204 ? null : res.json();
}

export const api = {
  // ---- auth / session (Google SSO handled by backend redirect) ----
  me: () => http("GET", "/me"),
  // Critical first-paint bundle in one round-trip: { me, settings, meMonth,
  // roles, teams, permissions, setup }. Replaces 7 separate calls.
  bootstrap: (year, month) => http("GET", `/bootstrap?year=${year}&month=${month}`),
  logout: () => http("POST", "/auth/logout"),
  // ---- UI language (persisted on the Person) ----
  setLang: (lang) => http("POST", "/me/lang", { lang }),
  // ---- first-time setup / factory reset (owner-only on the backend) ----
  setupStatus: () => http("GET", "/setup/status"),
  completeSetup: (payload) => http("POST", "/setup/complete", payload),
  resetSetup: (confirm) => http("POST", "/setup/reset", { confirm }),
  // Start real Google SSO by navigating the browser (not fetch — it's a redirect):
  //   window.location.href = `${import.meta.env.VITE_API_BASE_URL || ""}/api/auth/login`
  // Dev-only helpers (backend DEV_LOGIN=1) to sign in without Google:
  devUsers: () => http("GET", "/auth/dev-users"),
  devLogin: (personId) => http("POST", "/auth/dev-login", { personId }),

  // ---- people / org ----
  people: (params = "") => http("GET", "/people" + params),
  person: (id) => http("GET", `/people/${id}`),
  // Pre-add an employee (status "invited"); links by email on first SSO login.
  createPerson: (payload) => http("POST", "/people", payload),
  updatePerson: (id, patch) => http("PATCH", `/people/${id}`, patch),
  savePeriods: (id, periods) => http("PUT", `/people/${id}/periods`, { periods }),
  saveLeavePeriods: (id, leavePeriods) => http("PUT", `/people/${id}/leave-periods`, { leavePeriods }),
  teams: () => http("GET", "/teams"),
  assignTeam: (personId, team) => http("POST", `/people/${personId}/team`, { team }),

  // ---- attendance ----
  myMonth: (year, month) => http("GET", `/attendance/me?year=${year}&month=${month}`),
  personMonth: (id, year, month) => http("GET", `/attendance/${id}?year=${year}&month=${month}`),
  records: (scope, params = "") => http("GET", `/attendance/records?scope=${scope}${params}`),
  // Company-wide, status-only month rollup for the Everyone Calendar — readable
  // by any signed-in user (no fund/deduction figures, unlike records).
  calendar: (year, month) => http("GET", `/attendance/calendar?year=${year}&month=${month}`),
  override: (payload) => http("POST", "/attendance/override", payload),
  // HR worklist: incomplete punches + hourly-leave missing actual clocks for a month.
  missingCases: (year, month) => http("GET", `/attendance/missing-cases?year=${year}&month=${month}`),
  overridesList: (year, month) => http("GET", (year && month) ? `/attendance/overrides?year=${year}&month=${month}` : "/attendance/overrides"),
  // Raw device punches for a person on a day (HR picks hourly-leave actual clocks).
  dayPunches: (id, date) => http("GET", `/attendance/punches/${id}?date=${date}`),
  // Rebuild stored AttendanceDay rows from raw punches (after a logic change).
  // { personId? (else everyone), year?, month? (else all days) } -> {daysRebuilt}.
  recalcAttendance: (payload = {}) => http("POST", "/attendance/recalc", payload),

  // ---- requests / approvals ----
  myRequests: () => http("GET", "/requests/me"),
  createRequest: (payload) => http("POST", "/requests", payload),
  approvals: (scope) => http("GET", `/requests/pending?scope=${scope}`),
  decidedRequests: (scope) => http("GET", `/requests/decided?scope=${scope}`),
  decide: (id, decision, note) => http("POST", `/requests/${id}/decision`, { decision, note }),
  // Deactivate (suspend) or restore a decided request of any type.
  suspendRequest: (id, suspend) => http("POST", `/requests/${id}/suspend`, { suspend: suspend !== false }),
  // HR sets/corrects a hourly-leave request's actual clock-out / clock-back-in.
  setWorkClocks: (id, payload) => http("POST", `/requests/${id}/work-clocks`, payload),
  // HR flips whether an existing late/early/hourly-leave request spends a monthly
  // permission (first 60 min free) — fixes the day's deduction without re-filing.
  setRequestPermission: (id, usePermission) => http("POST", `/requests/${id}/permission`, { usePermission }),

  // ---- settings (fund / permissions / annual / shifts / daily grants / backfill) ----
  settings: () => http("GET", "/settings"),
  saveSettings: (next) => http("PUT", "/settings", next),
  // System config (Google Sheets export target) — gated by "system.settings".
  saveSystemSettings: (system) => http("PUT", "/settings/system", system),
  // The export service-account email (whom to share the sheet with) + server state.
  exportAccount: () => http("GET", "/settings/system/export-account"),
  // The linked spreadsheet's real title (read via the service account). Pass a url
  // to preview a freshly pasted link before saving; omit to use the stored one.
  sheetInfo: (url) =>
    http("GET", "/settings/system/sheet-info" + (url ? `?url=${encodeURIComponent(url)}` : "")),
  // ---- company hours report (HR/BOD; gated by "payroll.report") ----
  hoursReport: (year, month) => http("GET", `/attendance/hours-report?year=${year}&month=${month}`),
  // Default writes EVERYTHING: each day's Paid/PTO hours plus the OT and Lunch
  // summary columns — so the sheet's day grid is replaced from the app (the UI
  // confirms first). Pass metricsOnly:true to fill only OT/Lunch and leave the
  // day cells untouched, for months whose grid is being kept by hand.
  exportHoursReport: (year, month, { metricsOnly = false } = {}) =>
    http("POST", `/attendance/hours-report/export?year=${year}&month=${month}&metricsOnly=${metricsOnly ? 1 : 0}`),

  // ---- roles / RBAC ----
  roles: () => http("GET", "/roles"),
  permissions: () => http("GET", "/permissions"),
  saveRole: (role) => http("POST", "/roles", role),

  // ---- accounts / activation ----
  pendingAccounts: () => http("GET", "/accounts/pending"),
  activate: (personId, roleId) => http("POST", `/accounts/${personId}/activate`, { roleId }),
  deactivate: (personId) => http("POST", `/accounts/${personId}/deactivate`),

  // ---- notifications ----
  notifications: () => http("GET", "/notifications"),
  markAllRead: () => http("POST", "/notifications/read-all"),

  // ---- policies ----
  policies: () => http("GET", "/policies"),

  // ---- face-recognition devices / enrollments ----
  // The device key on the wire is now the `identifier` (the agent<->backend
  // matching key, = device.id in the UI). The serial (device_sn) is info-only,
  // set by the agent — HR endpoints never send it.
  devices: () => http("GET", "/devices"),
  mapDevice: (identifier, extId, personId) =>
    http("PUT", "/devices/map", { identifier, ext_id: extId, person_id: personId }),
  saveDevice: (identifier, { name, location } = {}) =>
    http("PUT", `/devices/${identifier}`, { name, location }),
  deleteDevice: (identifier) => http("DELETE", `/devices/${identifier}`),

  // ---- meeting-room booking ----
  meetingRooms: () => http("GET", "/meetings/rooms"),
  saveMeetingRooms: (rooms) => http("PUT", "/meetings/rooms", { rooms }),
  meetings: (start, end) => http("GET", `/meetings?start=${start}&end=${end}`),
  createMeeting: (payload) => http("POST", "/meetings", payload),
  updateMeeting: (id, payload) => http("PUT", `/meetings/${id}`, payload),
  deleteMeeting: (id) => http("DELETE", `/meetings/${id}`),
  // Attachments are multipart (File objects), so this one skips the JSON helper.
  uploadMeetingFiles: async (id, files) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    const res = await fetch(BASE + `/meetings/${id}/files`, { method: "POST", credentials: "include", body: fd });
    if (!res.ok) {
      const detail = await res.text().catch(() => "");
      throw Object.assign(new Error(`upload -> ${res.status}`), { status: res.status, detail });
    }
    return res.json();
  },
  deleteMeetingFile: (fid) => http("DELETE", `/meetings/files/${fid}`),
  meetingFileUrl: (fid) => BASE + `/meetings/files/${fid}`,
  // Calendar reconciliation (HR/BOD): Google meetings that aren't room bookings.
  meetingGcalEvents: (start, end) => http("GET", `/meetings/gcal-events?start=${start}&end=${end}`),
  reserveRoom: (payload) => http("POST", "/meetings/reserve", payload),
  offsiteFromEvent: (payload) => http("POST", "/meetings/offsite-from-event", payload),
  // ---- browser push (Web Push) ----
  pushConfig: () => http("GET", "/push/config"),
  pushSubscribe: (sub) => http("POST", "/push/subscribe", sub),
  pushUnsubscribe: (sub) => http("POST", "/push/unsubscribe", sub),
  pushTest: () => http("POST", "/push/test"),
};

export default api;
